<template>
  <div class="communications-page flex-grow">
    <Loading :loading="loading && !initialized">
      <div class="communications-shell">
        <div class="communications-header">
          <div class="communications-head-copy">
            <h2 class="communications-title">Коммуникации</h2>
            <div class="communications-subtitle">
              Постоянный чат iWALY и единая история внешних каналов {{ recipientLabel }}.
            </div>
          </div>
          <div class="recipient-meta">
            <span>{{ recipient.phone || 'Телефон не указан' }}</span>
            <span :class="['consent-badge', recipient.marketingAllowed ? 'ok' : 'muted']">
              {{ recipient.marketingAllowed ? 'Рекламные согласия активны' : 'Только сервисные сообщения' }}
            </span>
          </div>
        </div>

        <div
          v-if="identitySummary"
          :class="['identity-summary', identityLevelTone(identitySummary.level)]">
          <b>{{ identitySummary.label }}</b>
          <span>{{ identitySummary.privacyNotice }}</span>
        </div>

        <div ref="messengerLayout" class="messenger-layout" :style="viewportStyle">
          <section class="messenger-card">
            <div class="chat-toolbar">
              <div class="chat-search-wrap">
                <input
                  v-model.trim="historySearch"
                  type="search"
                  class="chat-search"
                  placeholder="Поиск по истории сообщений">
                <span v-if="historySearch" class="search-count">{{ filteredHistory.length }}</span>
              </div>
              <div class="refresh-actions">
                <span class="auto-refresh-state">
                  {{ autoRefreshActive ? 'Автообновление · 8 сек' : 'Автообновление приостановлено' }}
                </span>
                <button class="btn btn-sm btn-outline-primary" :disabled="loading || sending" @click="fetchData({ silent: true })">
                  Обновить
                </button>
              </div>
            </div>

            <div class="filter-row">
              <span class="filter-label">Каналы</span>
              <button type="button" class="filter-action" @click="selectAllHistoryChannels">Все</button>
              <button type="button" class="filter-action" @click="clearHistoryChannels">Сбросить</button>
              <button
                v-for="item in historyChannelOptions"
                :key="item.key"
                type="button"
                :class="['filter-chip', { active: isHistoryChannelSelected(item.key) }]"
                @click="toggleHistoryChannel(item.key)">
                {{ item.label }}
              </button>
              <label class="system-toggle">
                <input v-model="showSystemEvents" type="checkbox">
                Служебные события
              </label>
            </div>

            <div ref="historyScroll" class="chat-history" @scroll="onHistoryScroll">
              <div v-if="filteredHistory.length === 0" class="history-empty">
                {{
                  history.length
                    ? 'По выбранным фильтрам сообщений нет.'
                    : 'Коммуникаций с этим пользователем пока нет.'
                }}
              </div>

              <template v-for="group in groupedHistory">
                <div :key="`date-${group.key}`" class="date-divider"><span>{{ group.label }}</span></div>
                <template v-for="item in group.items">
                  <div
                    v-if="item.isSystemEvent"
                    :key="item.id"
                    class="system-event">
                    <button type="button" class="system-event-main" @click="toggleDetails(item.id)">
                      <span class="system-event-dot">•</span>
                      <span>{{ compactSystemText(item.message) }}</span>
                      <span class="system-event-time">{{ formatTime(item.sentAt || item.createdAt) }}</span>
                    </button>
                    <div v-if="expandedMessageId === item.id" class="technical-details system-details">
                      <div class="whitespace-pre-line" v-html="messageHtml(item.message)"></div>
                      <div>{{ technicalSummary(item) }}</div>
                    </div>
                  </div>

                  <div
                    v-else
                    :key="item.id"
                    :class="['chat-row', bubbleSide(item)]">
                    <div class="chat-bubble">
                      <div class="bubble-head">
                        <span class="bubble-name">{{ item.displayName || senderLabel(item) }}</span>
                        <span :class="['mini-channel', `channel-${item.channel}`]">{{ item.channelLabel || channelLabel(item.channel) }}</span>
                        <span
                          v-if="item.identityLabel"
                          :class="['identity-message-badge', identityLevelTone(item.identityLevel)]">
                          {{ item.identityLabel }}
                        </span>
                      </div>

                      <router-link
                        v-if="item.contextLabel && item.contextOrderId"
                        :to="`/orders/${item.contextOrderId}`"
                        class="history-context history-context-link">
                        {{ item.contextLabel }}
                      </router-link>
                      <span v-else-if="item.contextLabel" class="history-context">{{ item.contextLabel }}</span>

                      <div
                        v-if="item.channel === 'email'"
                        class="email-history-card">
                        <div class="email-card-title">
                          <span>✉ Письмо</span>
                          <b>{{ item.emailSubject || item.title || 'Без темы' }}</b>
                        </div>
                        <div class="email-card-preview">
                          {{ compactEmailPreview(item.message) }}
                        </div>
                        <div class="email-card-actions">
                          <button
                            type="button"
                            class="btn btn-xs btn-outline-primary"
                            @click="openEmail(item)">
                            Посмотреть
                          </button>
                          <button
                            v-if="item.senderType === 'user'
                              && emailChannel
                              && emailChannel.enabled"
                            type="button"
                            class="btn btn-xs btn-outline-secondary"
                            @click="replyToEmail(item)">
                            Ответить
                          </button>
                        </div>
                      </div>

                      <template v-else>
                        <div
                          v-if="item.title"
                          class="history-title">
                          {{ item.title }}
                        </div>
                        <div
                          v-if="item.message"
                          class="history-message whitespace-pre-line"
                          v-html="messageHtml(item.message)"></div>
                      </template>

                      <div v-if="item.attaches && item.attaches.length" class="history-images">
                        <template v-for="attach in item.attaches">
                          <button
                            v-if="isRemoteProviderAttachment(attach)
                              && !remoteAttachmentRequested(attach)"
                            :key="`attach-load-${attach.id}`"
                            type="button"
                            class="attachment-load-button"
                            @click="showRemoteAttachment(attach)">
                            Фото Talk-Me · показать
                          </button>
                          <a
                            v-else-if="!attachmentFailed(attach)"
                            :key="`attach-${attach.id}`"
                            :href="attach.url"
                            target="_blank"
                            rel="noopener">
                            <img
                              :src="attach.url"
                              loading="lazy"
                              alt="Вложение SupportChat"
                              @error="markAttachmentFailed(attach)">
                          </a>
                          <span
                            v-else
                            :key="`attach-missing-${attach.id}`"
                            class="attachment-unavailable">
                            {{ attachmentUnavailableLabel(attach) }}
                          </span>
                        </template>
                      </div>

                      <div class="bubble-foot">
                        <span>{{ formatTime(item.sentAt || item.createdAt) }}</span>
                        <button
                          v-if="hasReceipt(item)"
                          type="button"
                          :class="['receipt-button', `receipt-${receiptState(item)}`]"
                          :title="deliveryLabel(item)"
                          @click="toggleDetails(item.id)">
                          {{ deliveryIcon(item) }}
                        </button>
                        <button type="button" class="details-button" title="Подробности" @click="toggleDetails(item.id)">···</button>
                      </div>

                      <div v-if="expandedMessageId === item.id" class="technical-details">
                        <div v-if="item.legacy" class="detail-row">
                          <b>Legacy Talk-Me</b>
                          <span>{{ item.legacyProviderStatus || 'статус провайдера неизвестен' }}</span>
                        </div>
                        <div v-if="hasReceipt(item)" class="detail-row">
                          <b>Доставка</b><span>{{ deliveryLabel(item) }}</span>
                        </div>
                        <div v-if="item.providerLabel" class="detail-row"><b>Провайдер</b><span>{{ item.providerLabel }}</span></div>
                        <div v-if="item.source" class="detail-row"><b>Источник</b><span>{{ item.source }}</span></div>
                        <div v-if="item.providerMessageId" class="detail-row"><b>ID</b><span>{{ item.providerMessageId }}</span></div>
                        <div v-if="item.technical && item.technical.talkMeDialogId" class="detail-row"><b>Talk-Me dialog</b><span>{{ item.technical.talkMeDialogId }}</span></div>
                        <div v-if="item.error" class="history-error">{{ item.error }}</div>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>

            <div ref="composerDock" class="composer-dock">
              <div class="composer-topbar">
                <select v-model="channel" class="channel-select" @change="onChannelChanged">
                  <option v-for="item in channels" :key="item.key" :value="item.key" :disabled="!item.enabled">
                    {{ item.label }} · {{ item.enabled ? (item.providerLabel || 'доступен') : 'недоступен' }}
                  </option>
                </select>
                <span v-if="selectedChannel && !selectedChannel.enabled" class="channel-warning-inline">
                  {{ selectedChannel.reason || 'Канал недоступен' }}
                </span>
                <button
                  v-if="composerHasContent"
                  type="button"
                  class="btn btn-sm btn-link clear-composer"
                  :disabled="sending"
                  @click="clearActiveComposer">
                  Очистить поле
                </button>
              </div>

              <div
                v-if="selectedChannel && selectedChannel.privacyRisk"
                class="privacy-channel-warning">
                <b>⚠ Канал не подтверждает личность</b>
                <span>
                  {{
                    selectedChannel.privacyNotice
                      || 'Историю можно использовать как рабочий контекст, но не раскрывайте персональные данные без дополнительной проверки личности.'
                  }}
                </span>
              </div>

              <template v-if="channel === 'iwaly'">
                <textarea
                  v-model="iwalyMessage"
                  class="communication-textarea composer-textarea"
                  maxlength="4000"
                  :disabled="!selectedChannel || !selectedChannel.enabled || sending"
                  placeholder="Сообщение в приложение"
                  @keydown.ctrl.enter.prevent="send"
                  @keydown.meta.enter.prevent="send"></textarea>
                <div class="composer-note composer-input-meta">
                  <span>{{ iwalyMessage.length }}/4000</span>
                  <span>Ctrl/⌘+Enter — отправить</span>
                </div>

                <div class="composer-tools">
                  <label class="btn btn-sm btn-outline-primary mb-0">
                    Фото
                    <input type="file" accept="image/*" multiple hidden :disabled="sending" @change="selectIwalyFiles">
                  </label>
                  <span v-if="iwalyFiles.length" class="composer-note">Выбрано: {{ iwalyFiles.length }}</span>
                  <label class="iwaly-check compact">
                    <input
                      v-model="iwalySendPush"
                      type="checkbox"
                      :disabled="sending || !selectedChannel || !selectedChannel.pushAvailable">
                    PUSH
                  </label>
                  <label
                    class="iwaly-check compact"
                    title="Используйте только для действительно важных и срочных сообщений">
                    <input v-model="iwalyImportant" type="checkbox" :disabled="sending">
                    Важное
                  </label>
                </div>

                <div v-if="iwalyImportant" class="important-warning">
                  Важное — только для действительно срочных сообщений. Не злоупотребляйте этим режимом.
                </div>

              </template>

              <template v-else-if="isTalkMeChannel">
                <textarea
                  v-model="talkMeMessage"
                  class="communication-textarea composer-textarea"
                  maxlength="4000"
                  :disabled="!selectedChannel || !selectedChannel.enabled || sending"
                  :placeholder="`Ответ в ${selectedChannel ? selectedChannel.label : 'Talk-Me'}`"
                  @keydown.ctrl.enter.prevent="send"
                  @keydown.meta.enter.prevent="send"></textarea>
                <div class="composer-note composer-input-meta">
                  <span>{{ talkMeMessage.length }}/4000 · {{ selectedChannel ? selectedChannel.label : 'Talk-Me' }}</span>
                  <span>Ctrl/⌘+Enter — отправить</span>
                </div>
                <div v-if="selectedChannel" class="composer-note web-target-note">
                  <span v-if="selectedChannel.clientIdShort">
                    Ответ в последний trusted диалог выбранного канала
                    · {{ selectedChannel.clientIdShort }}
                  </span>
                  <span v-else>
                    Trusted диалог выбранного канала для ответа не определён.
                  </span>
                  <span v-if="selectedChannel.lastIncomingAt">
                    · входящее
                    {{ formatDateTime(selectedChannel.lastIncomingAt) }}
                  </span>
                </div>
                <div
                  v-if="selectedChannel
                    && selectedChannel.capabilities
                    && selectedChannel.capabilities.attachmentReason"
                  class="composer-note provider-capability-note">
                  {{ selectedChannel.capabilities.attachmentReason }}
                </div>
              </template>

              <template v-else-if="channel === 'email'">
                <div class="email-composer-meta">
                  <b>{{ emailModeLabel }}</b>
                  <span
                    v-if="selectedChannel
                      && selectedChannel.recipientEmail">
                    {{ selectedChannel.recipientEmail }}
                  </span>
                </div>

                <div
                  v-if="emailReplyContext"
                  class="email-reply-target">
                  <div>
                    <b>↩ Ответ на выбранное письмо</b>
                    <span>
                      {{ emailReplyContext.subject || 'Без темы' }}
                    </span>
                  </div>
                  <small>
                    <template v-if="emailReplyContext.dialogId">
                      Talk-Me dialog {{ emailReplyContext.dialogId }}
                    </template>
                    <template v-if="emailReplyContext.sentAt">
                      · {{ formatDateTime(emailReplyContext.sentAt) }}
                    </template>
                  </small>
                </div>

                <div
                  v-if="selectedChannel
                    && selectedChannel.warning"
                  class="important-warning email-address-warning">
                  ⚠ {{ selectedChannel.warning }}
                </div>

                <textarea
                  v-model="emailMessage"
                  class="communication-textarea composer-textarea email-textarea"
                  :maxlength="emailMaxLength"
                  :disabled="!selectedChannel
                    || !selectedChannel.enabled
                    || sending"
                  placeholder="Текст Email"
                  @keydown.ctrl.enter.prevent="send"
                  @keydown.meta.enter.prevent="send"></textarea>

                <div class="composer-note composer-input-meta">
                  <span>
                    {{ emailMessage.length }}/{{ emailMaxLength }}
                    · Email · Talk-Me
                  </span>
                  <span>Ctrl/⌘+Enter — отправить</span>
                </div>

                <details
                  v-if="selectedChannel
                    && selectedChannel.emailSignature"
                  class="email-signature-preview">
                  <summary>
                    Подпись iWALY добавится автоматически
                  </summary>
                  <pre>{{ selectedChannel.emailSignature }}</pre>
                </details>

                <div
                  v-if="selectedChannel
                    && selectedChannel.capabilities
                    && selectedChannel.capabilities.attachmentReason"
                  class="composer-note provider-capability-note">
                  {{ selectedChannel.capabilities.attachmentReason }}
                </div>
              </template>

              <template v-else-if="channel === 'sms'">
                <div
                  :class="['sms-editor', { disabled: !selectedChannel || !selectedChannel.enabled || sending }]">
                  <span class="sms-prefix" aria-hidden="true">{{ smsPrefix }}</span>
                  <textarea
                    v-model="smsMessage"
                    class="sms-body-input"
                    :maxlength="smsBodyMaxLength"
                    :disabled="!selectedChannel || !selectedChannel.enabled || sending"
                    placeholder="Введите текст SMS"
                    @keydown.ctrl.enter.prevent="send"
                    @keydown.meta.enter.prevent="send"></textarea>
                </div>
                <div class="composer-note composer-input-meta">
                  <span>{{ smsFullMessage.length }}/1120 · ≈ {{ smsSegments }} SMS</span>
                  <span>Ctrl/⌘+Enter — отправить</span>
                </div>
              </template>

              <template v-else-if="channel === 'whatsapp'">
                <div v-if="selectedChannel && selectedChannel.operatorFirstRisk" class="whatsapp-first-warning">
                  {{ selectedChannel.operatorFirstReason }}
                </div>
                <textarea
                  v-model="whatsappMessage"
                  class="communication-textarea composer-textarea whatsapp-textarea"
                  maxlength="4096"
                  :disabled="!selectedChannel || !selectedChannel.enabled || sending"
                  placeholder="Введите сообщение WhatsApp"
                  @input="handleWhatsappInput"
                  @keydown.ctrl.enter.prevent="send"
                  @keydown.meta.enter.prevent="send"></textarea>
                <div class="composer-note composer-input-meta">
                  <span>{{ whatsappMessage.length }}/4096 · Green API</span>
                  <span>Ctrl/⌘+Enter — отправить</span>
                </div>

                <div
                  v-if="selectedChannel
                    && selectedChannel.capabilities
                    && selectedChannel.capabilities.attachments"
                  class="composer-tools">
                  <label class="btn btn-sm btn-outline-primary mb-0">
                    Фото
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      :disabled="sending"
                      @change="selectIwalyFiles">
                  </label>
                  <span
                    v-if="iwalyFiles.length"
                    class="composer-note">
                    Выбрано: {{ iwalyFiles.length }}
                  </span>
                </div>
              </template>

              <template v-else-if="channel === 'push'">
                <input
                  v-model="pushTitle"
                  class="communication-input"
                  maxlength="120"
                  :disabled="!selectedChannel || !selectedChannel.enabled || sending"
                  placeholder="Заголовок (по умолчанию iWALY)">
                <textarea
                  v-model="pushMessage"
                  class="communication-textarea composer-textarea"
                  maxlength="1000"
                  :disabled="!selectedChannel || !selectedChannel.enabled || sending"
                  placeholder="Текст Push"
                  @keydown.ctrl.enter.prevent="send"
                  @keydown.meta.enter.prevent="send"></textarea>
                <div class="composer-note composer-input-meta">
                  <span>{{ pushMessage.length }}/1000</span>
                  <span>Ctrl/⌘+Enter — отправить</span>
                </div>
              </template>

              <div class="composer-actions">
                <div class="composer-note">
                  <template v-if="channel === 'iwaly' && selectedChannel">
                    Устройств: {{ selectedChannel.compatibleDevices || 0 }} · непрочитано поддержкой: {{ selectedChannel.unreadCount || 0 }}
                  </template>
                  <template v-else-if="isTalkMeChannel && selectedChannel">
                    {{
                      selectedChannel.enabled
                        ? `Прямой ответ: ${selectedChannel.label}`
                        : (selectedChannel.reason || 'Talk-Me канал недоступен')
                    }}
                  </template>
                  <template
                    v-else-if="channel === 'email' && selectedChannel">
                    {{ emailModeLabel }}
                  </template>
                  <template v-else-if="channel === 'push'">
                    {{ pushDevicesText }}
                  </template>
                </div>
                <button class="btn btn-primary communication-send" type="button" :disabled="!canSend || sending" @click="send">
                  {{ sending ? 'Отправляем…' : sendButtonLabel }}
                </button>
              </div>
            </div>
          </section>

          <aside class="context-sidebar">
            <section class="sidebar-card reply-assistant-card">
              <div class="sidebar-card-title">
                <span>Быстрые ответы</span>
                <small>{{ selectedChannel ? selectedChannel.label : 'Канал' }}</small>
              </div>
              <div class="sidebar-card-body reply-assistant-body">
                <template v-if="channel === 'sms'">
                  <div class="reply-section">
                    <div class="reply-section-title">Сервисные SMS · до 1 SMS</div>
                    <button
                      v-for="item in smsQuickReplies"
                      :key="item.key"
                      type="button"
                      class="sidebar-reply"
                      @click="applySmsQuickReply(item.message)">
                      <b>{{ item.title }}</b>
                      <span>{{ item.message }}</span>
                    </button>
                  </div>
                </template>

                <template v-else-if="channel === 'push'">
                  <div v-if="channelPresets.length" class="reply-section">
                    <div class="reply-section-title">Быстрые Push</div>
                    <button
                      v-for="item in channelPresets"
                      :key="`preset-${item.key}`"
                      type="button"
                      class="sidebar-reply"
                      @click="applyChannelPreset(item)">
                      <b>{{ item.label }}</b>
                      <span>{{ compactReplyText(item.message) }}</span>
                    </button>
                  </div>
                  <div v-else class="muted">Для Push быстрые ответы пока не настроены.</div>
                </template>

                <template v-else>
                  <div v-if="smartReplies.length" class="reply-section smart-replies">
                    <div class="reply-section-title">Умные ответы</div>
                    <button
                      v-for="item in smartReplies"
                      :key="`smart-${item.key}`"
                      type="button"
                      class="sidebar-reply smart"
                      @click="insertTemplate(item.message)">
                      <b>{{ item.reason }}</b>
                      <span>{{ compactReplyText(item.message) }}</span>
                    </button>
                  </div>

                  <div v-if="quickReplies.length" class="reply-section">
                    <div class="reply-section-title">Быстрые</div>
                    <button
                      v-for="item in quickReplies"
                      :key="`quick-${item.slot}`"
                      type="button"
                      class="sidebar-reply"
                      @click="insertTemplate(item.message)">
                      <b>{{ item.slot }}. {{ item.title }}</b>
                      <span>{{ compactReplyText(item.message) }}</span>
                    </button>
                  </div>

                  <details v-if="templates.length" class="reply-templates">
                    <summary>Все шаблоны · {{ templates.length }}</summary>
                    <div class="reply-template-list">
                      <button
                        v-for="item in templates"
                        :key="`template-${item.key}`"
                        type="button"
                        class="sidebar-reply template"
                        @click="insertTemplate(item.rendered || item.message)">
                        <b>{{ item.title }}</b>
                        <span>{{ compactReplyText(item.rendered || item.message) }}</span>
                      </button>
                    </div>
                  </details>

                  <div v-if="channel === 'whatsapp' && whatsappTemplates.length" class="reply-section provider-replies">
                    <div class="reply-section-title">Шаблоны WhatsApp</div>
                    <select
                      v-model="templateKey"
                      class="communication-select sidebar-template-select"
                      :disabled="!selectedChannel || !selectedChannel.enabled || sending"
                      @change="applyWhatsappTemplate">
                      <option value="">Выберите шаблон</option>
                      <option v-for="item in whatsappTemplates" :key="item.key" :value="item.key">{{ item.title }}</option>
                    </select>
                    <div v-if="selectedTemplate" class="sidebar-template-preview">
                      {{ compactReplyText(selectedTemplate.message, 220) }}
                    </div>
                  </div>

                  <div
                    v-if="!smartReplies.length && !quickReplies.length && !templates.length && !(channel === 'whatsapp' && whatsappTemplates.length)"
                    class="muted">
                    Для этого канала быстрые ответы пока не настроены.
                  </div>
                </template>
              </div>
            </section>

            <details class="sidebar-card mobile-collapsible user-card" open>
              <summary>Пользователь</summary>
              <div v-if="contextUser" class="sidebar-card-body">
                <div class="context-row"><span>Роль</span><b>{{ contextUser.role }}</b></div>
                <div class="context-row"><span>ФИО</span><b>{{ contextUser.name || '—' }}</b></div>
                <div class="context-row"><span>User ID</span><b>{{ contextUser.userId }}</b></div>
                <div class="context-row"><span>Телефон</span><b>{{ contextUser.phone || '—' }}</b></div>
                <div class="context-row"><span>Email</span><b class="truncate-value">{{ contextUser.email || '—' }}</b></div>
                <div class="context-row"><span>Регистрация</span><b>{{ contextUser.registeredAt || '—' }}</b></div>
                <div class="context-row"><span>Последний вход</span><b>{{ contextUser.lastLoginAt || 'Не зафиксирован' }}</b></div>
                <div class="context-row"><span>Через что вошёл</span><b>{{ entryMethodLabel(contextUser.lastLoginMethod) }}</b></div>
                <div class="context-row"><span>Платформа входа</span><b>{{ platformLabel(contextUser.lastLoginPlatform) }}</b></div>
                <div class="context-row"><span>Активность API</span><b>{{ contextUser.lastActivityAt || 'Не зафиксирована' }}</b></div>
                <div class="context-row"><span>Регистрация устройства</span><b>{{ contextUser.lastDeviceAt || 'Не зафиксирована' }}</b></div>
                <div class="context-row">
                  <span>Push-токен</span>
                  <b>{{ contextUser.pushTokenRegistered ? 'Токен сохранён (доставка не проверена)' : 'Токен не зарегистрирован' }}</b>
                </div>
                <div class="context-row"><span>Устройство</span><b>{{ platformLabel(contextUser.devicePlatform) }}</b></div>
                <div class="context-row">
                  <span>Версия приложения</span>
                  <b>{{ [contextUser.appVersion, contextUser.appBuild ? `(${contextUser.appBuild})` : null].filter(Boolean).join(' ') || 'Неизвестна' }}</b>
                </div>
                <div class="context-row"><span>Первый источник</span><b>{{ entrySourceLabel(contextUser.firstSource) }}</b></div>
                <div v-if="contextUser.firstPartnerId" class="context-row"><span>Партнёр</span><b>{{ contextUser.firstPartnerName || `ID ${contextUser.firstPartnerId}` }}</b></div>
                <div v-if="contextUser.firstEntrypoint" class="context-row">
                  <span>Точка регистрации</span>
                  <b>{{ contextUser.firstEntrypoint }}<span v-if="contextUser.firstFormVersion"> · {{ contextUser.firstFormVersion }}</span></b>
                </div>
                <div v-if="contextUser.registrationMethod" class="context-row"><span>Регистрация через</span><b>{{ entryMethodLabel(contextUser.registrationMethod) }}</b></div>
                <div v-if="contextUser.lastOrderChannel" class="context-row"><span>Канал последнего заказа</span><b>{{ entrySourceLabel(contextUser.lastOrderChannel) }}</b></div>
                <div v-if="contextUser.sourceConfidence" class="context-row">
                  <span>Источник данных</span>
                  <b>{{ sourceConfidenceLabel(contextUser.sourceConfidence) }}</b>
                </div>
                <div class="context-row"><span>Непрочитано</span><b>{{ contextUser.unreadCount || 0 }}</b></div>
              </div>
            </details>

            <details class="sidebar-card mobile-collapsible orders-card" open>
              <summary>Последние заказы</summary>
              <div class="sidebar-card-body">
                <div v-if="!recentOrders.length" class="muted">Нет заказов</div>
                <div v-for="order in recentOrders" :key="order.id" class="order-mini">
                  <router-link :to="`/orders/${order.id}`" class="order-link order-summary">
                    <div><b>#{{ order.id }}</b> · {{ order.status || '—' }}</div>
                    <div>Сумма: {{ order.amount || '—' }} · оценка {{ order.rating || '—' }}</div>
                  </router-link>
                  <div
                    v-if="recipientType === 'customer' && canUseOrderPayments"
                    class="payment-actions">
                    <button type="button" class="payment-action" @click="copyPaymentLink(order)">Копировать</button>
                    <button type="button" class="payment-action" @click="insertPaymentLink(order)">В чат</button>
                    <button type="button" class="payment-action" @click="openPaymentLink(order)">Открыть</button>
                  </div>
                </div>
              </div>
            </details>


            <details class="sidebar-card mobile-collapsible">
              <summary>Talk-Me</summary>
              <div class="sidebar-card-body">
                <button type="button" class="btn btn-sm btn-outline-primary w-100" :disabled="importing" @click="importTalkMe">
                  {{ importing ? 'Импортируем…' : 'Обновить legacy-историю' }}
                </button>
                <div class="composer-note mt-2">
                  Повторный импорт не создаёт дубли и может уточнить канал старых сообщений.
                </div>
              </div>
            </details>
          </aside>
        </div>
      </div>
    </Loading>

    <div
      v-if="emailModalItem"
      class="email-modal-backdrop"
      @click.self="closeEmail">
      <section class="email-modal">
        <header class="email-modal-head">
          <div>
            <small>Email · Talk-Me</small>
            <h3>
              {{
                emailModalItem.emailSubject
                  || emailModalItem.title
                  || 'Без темы'
              }}
            </h3>
            <div class="email-modal-meta">
              {{
                emailModalItem.displayName
                  || senderLabel(emailModalItem)
              }}
              ·
              {{
                formatDateTime(
                  emailModalItem.sentAt
                    || emailModalItem.createdAt
                )
              }}
            </div>
          </div>

          <button
            type="button"
            class="email-modal-close"
            @click="closeEmail">
            ×
          </button>
        </header>

        <div class="email-modal-body">
          <div
            class="email-full-text whitespace-pre-line"
            v-html="messageHtml(emailModalItem.message)"></div>

          <div
            v-if="emailModalItem.attaches
              && emailModalItem.attaches.length"
            class="history-images email-modal-images">
            <template v-for="attach in emailModalItem.attaches">
              <button
                v-if="isRemoteProviderAttachment(attach)
                  && !remoteAttachmentRequested(attach)"
                :key="`email-attach-load-${attach.id}`"
                type="button"
                class="attachment-load-button"
                @click="showRemoteAttachment(attach)">
                Фото Talk-Me · показать
              </button>
              <a
                v-else-if="!attachmentFailed(attach)"
                :key="`email-attach-${attach.id}`"
                :href="attach.url"
                target="_blank"
                rel="noopener">
                <img
                  :src="attach.url"
                  loading="lazy"
                  alt="Вложение Email"
                  @error="markAttachmentFailed(attach)">
              </a>
              <span
                v-else
                :key="`email-attach-missing-${attach.id}`"
                class="attachment-unavailable">
                {{ attachmentUnavailableLabel(attach) }}
              </span>
            </template>
          </div>
        </div>

        <footer class="email-modal-actions">
          <button
            class="btn btn-sm btn-outline-secondary"
            @click="closeEmail">
            Закрыть
          </button>
          <button
            v-if="emailModalItem.senderType === 'user'
              && emailChannel
              && emailChannel.enabled"
            class="btn btn-sm btn-primary"
            @click="replyToEmail(emailModalItem)">
            Ответить по Email
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import Loading from '@/components/Loading.vue';
import {
  customerCommunications,
  sendCustomerCommunication,
  importCustomerTalkMe,
  uploadCustomerCommunicationImages,
  customerSupportRead,
} from '@/api/customers';
import {
  contractorCommunications,
  sendContractorCommunication,
  importContractorTalkMe,
  uploadContractorCommunicationImages,
  contractorSupportRead,
} from '@/api/contractors';
import { orderPaymentLink } from '@/api/orders';
import { ADMIN_PERMISSIONS, canAccess } from '@/access/adminAccess';
import { iwalyNotify } from '@/notify';

export default {
  components: { Loading },
  props: {
    userId: { type: [Number, String], required: true },
    recipientType: {
      type: String,
      required: true,
      validator: (value) => ['customer', 'contractor'].includes(value),
    },
    recipientLabel: { type: String, default: 'пользователю' },
  },
  data() {
    return {
      loading: true,
      initialized: false,
      sending: false,
      recipient: {},
      channels: [],
      history: [],
      channel: 'iwaly',
      smsMessage: '',
      templateKey: '',
      whatsappMessage: '',
      pushTitle: '',
      pushMessage: '',
      iwalyMessage: '',
      talkMeMessage: '',
      emailMessage: '',
      emailModalItem: null,
      emailReplyExternalContactId: null,
      emailReplyClientId: null,
      emailReplyDialogId: null,
      emailReplyContext: null,
      attachmentImageErrors: {},
      remoteAttachmentRequests: {},
      iwalySendPush: true,
      iwalyImportant: false,
      iwalyFiles: [],
      iwalyAttachments: [],
      context: {},
      lastChannels: [],
      identityAssociations: [],
      identitySummary: null,
      importing: false,
      historySearch: '',
      historyChannelFilters: [],
      showSystemEvents: false,
      expandedMessageId: null,
      pollTimer: null,
      autoRefreshActive: true,
      userScrolledAwayFromBottom: false,
      lastInboundMessageToken: null,
      viewportHeight: null,
    };
  },
  computed: {
    ...mapState({ currentAdmin: (state) => state.auth.user }),
    selectedChannel() { return this.channels.find((item) => item.key === this.channel) || null; },
    isTalkMeChannel() { return ['web', 'max', 'vk', 'meta'].includes(this.channel); },
    isEmailChannel() { return this.channel === 'email'; },
    emailChannel() {
      return this.channels.find((item) => item.key === 'email') || null;
    },
    emailMaxLength() {
      return Math.max(
        1,
        Number((this.selectedChannel && this.selectedChannel.maxLength) || 2200),
      );
    },
    emailModeLabel() {
      if (!this.selectedChannel) return 'Email';
      if (this.selectedChannel.replyMode === 'email_reply') {
        return 'Ответ в существующий Email-диалог Talk-Me';
      }
      return this.selectedChannel.firstSendTransport === 'smtp'
        ? 'Email через SMTP'
        : 'Новое письмо через Talk-Me';
    },
    contextUser() { return (this.context && this.context.user) || null; },
    recentOrders() { return (this.context && this.context.orders) || []; },
    canUseOrderPayments() { return canAccess(this.currentAdmin, ADMIN_PERMISSIONS.ORDERS); },
    quickReplies() { return (this.context && this.context.quickReplies) || []; },
    templates() { return (this.context && this.context.templates) || []; },
    smartReplies() { return (this.context && this.context.smartReplies) || []; },
    whatsappTemplates() {
      const item = this.channels.find((channel) => channel.key === 'whatsapp');
      return item && Array.isArray(item.templates) ? item.templates : [];
    },
    selectedTemplate() { return this.whatsappTemplates.find((item) => item.key === this.templateKey) || null; },
    pushDevicesText() {
      const item = this.channels.find((channel) => channel.key === 'push');
      const count = item && item.mobileDevicesCount ? item.mobileDevicesCount : 0;
      return count > 0
        ? `Зарегистрировано мобильных устройств: ${count}`
        : 'Нет активного мобильного устройства.';
    },
    smsPrefix() { return 'iWALY: '; },
    smsQuickReplies() {
      return [
        { key: 'contact-support', title: 'Связаться с нами', message: 'Пожалуйста, свяжитесь с поддержкой iWALY.' },
        { key: 'order-chat-reply', title: 'Нужен ответ', message: 'Требуется ваш ответ в чате заказа. Откройте iWALY.' },
        { key: 'order-clarify', title: 'Уточнение по заказу', message: 'По заказу требуется уточнение. Откройте чат iWALY.' },
        { key: 'support-message', title: 'Новое сообщение', message: 'У вас новое сообщение от поддержки. Откройте iWALY.' },
        { key: 'order-action', title: 'Требуется действие', message: 'Требуется действие по заказу. Откройте чат iWALY.' },
      ];
    },
    communicationPresets() { return (this.context && this.context.communicationPresets) || []; },
    channelPresets() {
      const key = this.isTalkMeChannel ? 'iwaly' : this.channel;
      return this.communicationPresets
        .map((item) => {
          const payload = item && item.channels ? item.channels[key] : null;
          if (!payload || !payload.message) return null;
          return {
            key: item.key,
            label: item.label,
            title: payload.title || '',
            message: payload.message,
          };
        })
        .filter(Boolean);
    },
    smsBodyMaxLength() { return 1120 - this.smsPrefix.length; },
    smsFullMessage() { return `${this.smsPrefix}${this.smsMessage}`; },
    smsSegments() {
      const message = this.smsFullMessage;
      const { length } = message;
      if (!this.smsMessage.trim()) return 0;
      const unicode = Array.from(message).some((character) => character.charCodeAt(0) > 127);
      const single = unicode ? 70 : 160;
      const multi = unicode ? 67 : 153;
      return length <= single ? 1 : Math.ceil(length / multi);
    },
    canSend() {
      if (!this.selectedChannel || !this.selectedChannel.enabled) return false;
      if (this.channel === 'iwaly') return this.iwalyMessage.trim().length > 0 || this.iwalyFiles.length > 0 || this.iwalyAttachments.length > 0;
      if (this.isTalkMeChannel) return this.talkMeMessage.trim().length > 0;
      if (this.channel === 'email') return this.emailMessage.trim().length > 0;
      if (this.channel === 'sms') return this.smsMessage.trim().length > 0;
      if (this.channel === 'whatsapp') {
        return this.whatsappMessage.trim().length > 0
          || this.iwalyFiles.length > 0
          || this.iwalyAttachments.length > 0;
      }
      if (this.channel === 'push') return this.pushMessage.trim().length > 0;
      return false;
    },
    composerHasContent() {
      if (this.channel === 'iwaly') {
        return this.iwalyMessage.trim() !== ''
          || this.iwalyFiles.length > 0
          || this.iwalyAttachments.length > 0;
      }
      if (this.isTalkMeChannel) return this.talkMeMessage.trim() !== '';
      if (this.channel === 'email') return this.emailMessage.trim() !== '';
      if (this.channel === 'sms') return this.smsMessage.trim() !== '';
      if (this.channel === 'whatsapp') {
        return this.whatsappMessage.trim() !== ''
          || this.templateKey !== ''
          || this.iwalyFiles.length > 0
          || this.iwalyAttachments.length > 0;
      }
      if (this.channel === 'push') {
        return this.pushMessage.trim() !== '' || this.pushTitle.trim() !== '';
      }
      return false;
    },
    sendButtonLabel() {
      if (this.channel === 'iwaly') return 'Написать в iWALY';
      if (this.isTalkMeChannel) return `Ответить в ${this.selectedChannel ? this.selectedChannel.label : 'Talk-Me'}`;
      if (this.channel === 'email') {
        return this.selectedChannel
          && this.selectedChannel.replyMode === 'email_reply'
          ? 'Ответить по Email'
          : 'Отправить Email';
      }
      if (this.channel === 'sms') return 'Отправить SMS';
      if (this.channel === 'whatsapp') return 'Отправить WhatsApp';
      if (this.channel === 'push') return 'Отправить Push';
      return 'Отправить';
    },
    historyChannelOptions() {
      const labels = {};
      this.lastChannels.forEach((item) => { if (item.channel) labels[item.channel] = item.label || this.channelLabel(item.channel); });
      this.history.forEach((item) => { if (item.channel) labels[item.channel] = item.channelLabel || this.channelLabel(item.channel); });
      return Object.keys(labels).map((key) => ({ key, label: labels[key] })).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
    },
    filteredHistory() {
      const query = this.historySearch.trim().toLocaleLowerCase('ru');
      const selected = this.historyChannelFilters;
      return this.history.filter((item) => {
        if (!this.showSystemEvents && item.isSystemEvent) return false;
        if (selected.length && !selected.includes(item.channel || 'iwaly')) return false;
        if (!query) return true;
        const haystack = [item.message, item.title, item.displayName, item.channelLabel, item.providerLabel, item.contextLabel]
          .filter(Boolean).join(' ').toLocaleLowerCase('ru');
        return haystack.includes(query);
      }).sort((a, b) => {
        const left = moment(a.sentAt || a.createdAt);
        const right = moment(b.sentAt || b.createdAt);
        if (left.isValid() && right.isValid() && left.valueOf() !== right.valueOf()) return left.valueOf() - right.valueOf();
        return String(a.id || '').localeCompare(String(b.id || ''));
      });
    },
    viewportStyle() {
      return this.viewportHeight ? { '--communications-viewport-height': `${this.viewportHeight}px` } : {};
    },
    groupedHistory() {
      const groups = [];
      let current = null;
      this.filteredHistory.forEach((item) => {
        const date = moment(item.sentAt || item.createdAt);
        const key = date.isValid() ? date.format('YYYY-MM-DD') : 'unknown';
        if (!current || current.key !== key) {
          current = { key, label: this.formatDateLabel(date), items: [] };
          groups.push(current);
        }
        current.items.push(item);
      });
      return groups;
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('resize', this.updateViewportHeight);
    window.addEventListener('orientationchange', this.updateViewportHeight);
    this.updateViewportHeight();
    this.startPolling();
  },
  beforeDestroy() {
    this.persistHistoryScroll();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('resize', this.updateViewportHeight);
    window.removeEventListener('orientationchange', this.updateViewportHeight);
    this.stopPolling();
  },
  methods: {
    entrySourceLabel(value) {
      return {
        partner_form: 'Партнёрская форма',
        android_app: 'Приложение Android',
        ios_app: 'Приложение iOS',
        iwaly_web: 'Web iWALY',
        iwaly_app_unknown: 'Приложение (платформа неизвестна)',
        max_miniapp_form_v2: 'Форма V2 внутри MAX',
        partner_form_v2: 'Партнёрская форма V2',
        partner_form_legacy: 'Партнёрская форма (стар.)',
        max_miniapp: 'MAX Mini App',
        direct_registration: 'Прямая регистрация',
        external_registration: 'Регистрация через внешний сервис',
        partner_external_registration: 'Внешняя регистрация с партнёром',
      }[value] || value || 'Не подтверждён';
    },
    entryMethodLabel(value) {
      return {
        max: 'MAX',
        sber: 'Sber ID',
        mobile_id: 'Mobile ID',
        phone_password: 'Телефон и пароль',
        phone_password_registration: 'Телефон и пароль',
        partner_order_form: 'Партнёрская форма',
        web_handoff: 'Web-переход по заказу',
        external_handoff: 'Внешняя авторизация',
      }[value] || value || 'Неизвестен';
    },
    sourceConfidenceLabel(value) {
      return {
        server_observed: 'Зафиксирован backend',
        client_reported: 'Заявлен клиентом',
      }[value] || 'Исторические данные';
    },
    platformLabel(value) {
      return { android: 'Android', ios: 'iOS', web: 'Web' }[value] || value || 'Неизвестна';
    },
    updateViewportHeight() {
      this.$nextTick(() => {
        const layout = this.$refs.messengerLayout;
        if (!layout || typeof window === 'undefined') return;
        const { top } = layout.getBoundingClientRect();
        const available = Math.floor(window.innerHeight - top - 12);
        const mobileExtra = window.innerWidth <= 600 ? 92 : 0;
        const minimum = window.innerWidth <= 600 ? 500 : 360;
        this.viewportHeight = Math.max(minimum, available + mobileExtra);
      });
    },
    historyScrollKey(userId = this.userId) {
      return `iwaly-admin:communications:history-scroll:${this.recipientType}:${userId}`;
    },
    captureHistoryScrollState() {
      const el = this.$refs.historyScroll;
      if (!el) return null;
      return {
        top: el.scrollTop,
        nearBottom: this.isHistoryNearBottom(),
      };
    },
    readStoredHistoryScroll() {
      if (typeof window === 'undefined' || !window.sessionStorage) return null;
      try {
        const raw = window.sessionStorage.getItem(this.historyScrollKey());
        if (!raw) return null;
        const state = JSON.parse(raw);
        if (!state || !Number.isFinite(Number(state.top))) return null;
        return {
          top: Math.max(0, Number(state.top)),
          nearBottom: state.nearBottom === true,
        };
      } catch (error) {
        return null;
      }
    },
    persistHistoryScroll(userId = this.userId) {
      if (typeof window === 'undefined' || !window.sessionStorage || !userId) return;
      const state = this.captureHistoryScrollState();
      if (!state) return;
      try {
        window.sessionStorage.setItem(
          this.historyScrollKey(userId),
          JSON.stringify(state),
        );
      } catch (error) { /* sessionStorage may be unavailable in private modes */ }
    },
    restoreHistoryScrollState(state) {
      const el = this.$refs.historyScroll;
      if (!el || !state) return;
      if (state.nearBottom) {
        this.scrollHistoryToBottom();
        return;
      }
      const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = Math.min(Math.max(0, Number(state.top) || 0), maxTop);
      this.userScrolledAwayFromBottom = true;
      this.persistHistoryScroll();
    },
    restorePageScroll(top) {
      if (typeof window === 'undefined' || !Number.isFinite(Number(top))) return;
      const target = Math.max(0, Number(top));
      window.requestAnimationFrame(() => {
        window.scrollTo(0, target);
      });
    },
    communicationsUploadApi(payload) {
      return this.recipientType === 'contractor'
        ? uploadContractorCommunicationImages(
          this.userId,
          payload,
        )
        : uploadCustomerCommunicationImages(
          this.userId,
          payload,
        );
    },
    supportReadApi(messageIds, externalContactIds = []) {
      return this.recipientType === 'contractor'
        ? contractorSupportRead(this.userId, messageIds, externalContactIds)
        : customerSupportRead(this.userId, messageIds, externalContactIds);
    },
    fetchApi() {
      return this.recipientType === 'contractor'
        ? contractorCommunications(this.userId)
        : customerCommunications(this.userId);
    },
    sendApi(payload) {
      return this.recipientType === 'contractor'
        ? sendContractorCommunication(this.userId, payload)
        : sendCustomerCommunication(this.userId, payload);
    },
    selectIwalyFiles(event) {
      const input = event.target;
      this.iwalyFiles = Array.from(input.files || []).slice(0, 10);
      input.value = '';
    },
    clearIwalyFiles() { this.iwalyFiles = []; this.iwalyAttachments = []; },
    clearActiveComposer() {
      if (this.channel === 'iwaly') {
        this.iwalyMessage = '';
        this.iwalyImportant = false;
        this.clearIwalyFiles();
        return;
      }
      if (this.isTalkMeChannel) {
        this.talkMeMessage = '';
        return;
      }
      if (this.channel === 'email') {
        this.emailMessage = '';
        this.emailReplyExternalContactId = null;
        this.emailReplyClientId = null;
        this.emailReplyDialogId = null;
        this.emailReplyContext = null;
        this.clearIwalyFiles();
        return;
      }
      if (this.channel === 'sms') {
        this.smsMessage = '';
        return;
      }
      if (this.channel === 'whatsapp') {
        this.whatsappMessage = '';
        this.templateKey = '';
        this.clearIwalyFiles();
        return;
      }
      if (this.channel === 'push') {
        this.pushTitle = '';
        this.pushMessage = '';
      }
    },
    async uploadIwalyFiles() {
      if (!this.iwalyFiles.length) return;
      const fd = new FormData();
      this.iwalyFiles.forEach((file, index) => fd.append(`file${index}`, file));
      const response = await this.communicationsUploadApi(fd);
      this.iwalyAttachments = (response.data && response.data.attachments) || [];
      this.iwalyFiles = [];
    },
    async acknowledgeUserMessages() {
      const ids = this.history
        .filter((item) => String(item.id || '').startsWith('iwaly-')
          && !item.legacy
          && item.senderType === 'user'
          && item.rawId
          && !item.adminReadAt)
        .map((item) => item.rawId);
      const externalContactIds = Array.from(new Set(this.history
        .filter((item) => item
          && !item.historicalArchive
          && !item.readOnly
          && item.senderType === 'user'
          && item.associatedExternalContactId
          && !item.adminReadAt)
        .map((item) => Number(item.associatedExternalContactId))
        .filter((id) => Number.isInteger(id) && id > 0)));
      if (!ids.length && !externalContactIds.length) return;

      const markedIds = ids.slice(-200);
      const markedExternalContactIds = externalContactIds.slice(-100);
      try {
        const response = await this.supportReadApi(markedIds, markedExternalContactIds);
        const unreadCount = Number((response.data && response.data.unreadCount) || 0);
        const readIds = new Set(markedIds.map((id) => String(id)));
        const readExternalIds = new Set(markedExternalContactIds.map((id) => String(id)));
        const readAt = new Date().toISOString();

        this.history = this.history.map((item) => (
          readIds.has(String(item.rawId || ''))
            || readExternalIds.has(String(item.associatedExternalContactId || ''))
            ? { ...item, adminReadAt: item.adminReadAt || readAt }
            : item
        ));
        this.channels = this.channels.map((item) => (
          item.key === 'iwaly' ? { ...item, unreadCount } : item
        ));
        if (this.context && this.context.user) {
          this.context = {
            ...this.context,
            user: { ...this.context.user, unreadCount },
          };
        }

        // When this component is embedded in the Communications split-pane,
        // refresh the inbox immediately instead of waiting for its 15s poll.
        this.refreshParentInbox();
      } catch (e) { /* retry on next refresh */ }
    },
    refreshParentInbox() {
      let parent = this.$parent;
      let hops = 0;
      while (parent && hops < 8) {
        if (Array.isArray(parent.items) && typeof parent.load === 'function') {
          parent.load(false);
          return;
        }
        parent = parent.$parent;
        hops += 1;
      }
    },
    latestInboundMessage(items = this.history) {
      return (Array.isArray(items) ? items : [])
        .filter((item) => item && item.senderType === 'user' && item.channel)
        .slice()
        .sort((left, right) => {
          const leftDate = moment(left.sentAt || left.createdAt);
          const rightDate = moment(right.sentAt || right.createdAt);
          const leftTime = leftDate.isValid() ? leftDate.valueOf() : 0;
          const rightTime = rightDate.isValid() ? rightDate.valueOf() : 0;
          if (leftTime !== rightTime) return leftTime - rightTime;
          return String(left.rawId || left.id || '').localeCompare(String(right.rawId || right.id || ''));
        })
        .pop() || null;
    },
    inboundMessageToken(item) {
      if (!item) return null;
      return [
        item.rawId || item.id || '',
        item.channel || '',
        item.sentAt || item.createdAt || '',
      ].join('|');
    },
    autoSelectReplyChannelFromLatestInbound(options = {}) {
      const latest = this.latestInboundMessage(this.history);
      const token = this.inboundMessageToken(latest);
      if (!latest || !token) {
        this.lastInboundMessageToken = null;
        return;
      }

      const isNewInbound = token !== this.lastInboundMessageToken;
      const shouldSelect = options.force === true || !this.initialized || isNewInbound;
      const channelState = this.channels.find((item) => item.key === latest.channel);
      if (shouldSelect && !latest.readOnly && channelState && channelState.enabled) {
        this.channel = latest.channel;
      }
      this.lastInboundMessageToken = token;
    },
    async fetchData(options = {}) {
      const silent = options && options.silent === true;
      const wasInitialized = this.initialized;
      const currentScroll = this.captureHistoryScrollState();
      const storedScroll = !wasInitialized ? this.readStoredHistoryScroll() : null;
      const pageScrollX = silent && typeof window !== 'undefined' ? window.scrollX : null;
      const pageScrollY = silent && typeof window !== 'undefined' ? window.scrollY : null;
      if (!silent) this.loading = true;
      try {
        const response = await this.fetchApi();
        const data = response.data || {};
        this.recipient = data.recipient || {};
        this.channels = data.channels || [];
        this.history = data.history || [];
        this.context = data.context || {};
        this.lastChannels = data.lastChannels || [];
        this.identityAssociations = data.identityAssociations || [];
        this.identitySummary = data.identitySummary || null;
        const exists = this.channels.some((item) => item.key === this.channel);
        if (!exists && this.channels.length) this.channel = this.channels[0].key;
        this.autoSelectReplyChannelFromLatestInbound();
        this.initialized = true;
        this.$nextTick(() => {
          if (!silent) this.updateViewportHeight();
          this.acknowledgeUserMessages();

          if (silent && currentScroll) {
            this.restoreHistoryScrollState(currentScroll);
          } else if (!wasInitialized && storedScroll) {
            this.restoreHistoryScrollState(storedScroll);
          } else {
            this.scrollHistoryToBottom();
          }

          if (silent && typeof window !== 'undefined' && pageScrollX !== null && pageScrollY !== null
            && (window.scrollX !== pageScrollX || window.scrollY !== pageScrollY)) {
            window.scrollTo(pageScrollX, pageScrollY);
          }
        });
      } catch (error) {
        if (!silent) iwalyNotify.error({ text: 'Не удалось загрузить коммуникации пользователя' });
      } finally {
        if (!silent) this.loading = false;
      }
    },
    payload(confirmUnverifiedEmail = false) {
      if (this.channel === 'iwaly') {
        return {
          channel: 'iwaly',
          message: this.iwalyMessage.trim(),
          priority: this.iwalyImportant ? 'important' : 'normal',
          sendPush: this.iwalySendPush,
          attaches: this.iwalyAttachments.map((item) => item.id),
        };
      }
      if (this.isTalkMeChannel) {
        return {
          channel: this.channel,
          message: this.talkMeMessage.trim(),
          clientId: (this.selectedChannel && this.selectedChannel.clientId) || null,
          externalContactId: (this.selectedChannel && this.selectedChannel.externalContactId) || null,
        };
      }
      if (this.channel === 'email') {
        return {
          channel: 'email',
          message: this.emailMessage.trim(),
          externalContactId: (
            this.emailReplyExternalContactId
              || (this.selectedChannel
                && this.selectedChannel.externalContactId)
          ) || null,
          clientId: (
            this.emailReplyClientId
              || (this.selectedChannel
                && this.selectedChannel.clientId)
          ) || null,
          dialogId: (
            this.emailReplyDialogId
              || (this.selectedChannel
                && this.selectedChannel.dialogId)
          ) || null,
          expectedReplyMode: (
            this.selectedChannel
              && this.selectedChannel.replyMode
          ) || null,
          confirmUnverifiedEmail,
        };
      }

      if (this.channel === 'sms') return { channel: 'sms', message: `${this.smsPrefix}${this.smsMessage.trim()}` };
      if (this.channel === 'whatsapp') {
        return {
          channel: 'whatsapp',
          message: this.whatsappMessage.trim(),
          templateKey: this.templateKey || null,
          attaches: this.iwalyAttachments.map((item) => item.id),
        };
      }
      if (this.channel === 'push') return { channel: 'push', title: this.pushTitle.trim() || null, message: this.pushMessage.trim() };
      return { channel: this.channel };
    },
    identityLevelTone(level) {
      if (level === 'conflict') return 'danger';
      if (level === 'confirmed' || level === 'channel_confirmed') return 'success';
      return 'warning';
    },
    compactReplyText(text, limit = 120) {
      const value = String(text || '').replace(/\s+/g, ' ').trim();
      return value.length > limit ? `${value.slice(0, limit - 1)}…` : value;
    },
    applySmsQuickReply(text) {
      this.channel = 'sms';
      this.smsMessage = String(text || '').slice(0, this.smsBodyMaxLength);
    },
    insertTemplate(text, closeTemplates = false) {
      const value = String(text || '');
      if (!value) return;

      if (this.isTalkMeChannel) this.talkMeMessage = value;
      else if (this.channel === 'email') this.emailMessage = value;
      else if (this.channel === 'whatsapp') {
        this.whatsappMessage = value;
        this.templateKey = '';
      } else if (this.channel === 'push') this.pushMessage = value;
      else if (this.channel === 'iwaly') this.iwalyMessage = value;
      else return;

      if (closeTemplates) {
        this.$nextTick(() => {
          const details = this.$refs.templatesBox;
          if (details) details.open = false;
        });
      }
    },
    applyChannelPreset(item) {
      if (!item || !item.message) return;
      if (this.channel === 'push') {
        this.pushTitle = item.title || this.pushTitle;
        this.pushMessage = item.message;
      }
    },
    applyWhatsappTemplate() {
      const template = this.selectedTemplate;
      if (!template) return;
      this.whatsappMessage = String(template.message || '');
    },
    handleWhatsappInput() {
      const template = this.selectedTemplate;
      if (!template) return;
      if (this.whatsappMessage.trim() !== String(template.message || '').trim()) this.templateKey = '';
    },
    paymentUrl(response) {
      const raw = response && response.data && response.data.url;
      if (!raw) throw new Error('Payment URL missing');
      return decodeURIComponent(raw);
    },
    async fetchPaymentUrl(order) {
      if (!order || !order.id || !this.canUseOrderPayments) return null;
      const response = await orderPaymentLink(order.id);
      return this.paymentUrl(response);
    },
    async copyText(value) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        return;
      }
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', 'readonly');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    },
    async copyPaymentLink(order) {
      try {
        const url = await this.fetchPaymentUrl(order);
        if (!url) return;
        await this.copyText(url);
        iwalyNotify.success({ text: `Ссылка оплаты заказа №${order.id} скопирована` });
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось получить ссылку оплаты' });
      }
    },
    async insertPaymentLink(order) {
      try {
        const url = await this.fetchPaymentUrl(order);
        if (!url) return;
        const line = `Ссылка для оплаты заказа №${order.id}: ${url}`;
        if (this.isTalkMeChannel) {
          this.talkMeMessage = [this.talkMeMessage.trim(), line].filter(Boolean).join('\n\n');
        } else {
          this.iwalyMessage = [this.iwalyMessage.trim(), line].filter(Boolean).join('\n\n');
          this.channel = 'iwaly';
        }
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось получить ссылку оплаты' });
      }
    },
    async openPaymentLink(order) {
      try {
        const url = await this.fetchPaymentUrl(order);
        if (!url) return;
        window.open(url, '_blank', 'noopener');
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось получить ссылку оплаты' });
      }
    },
    async importTalkMe() {
      this.importing = true;
      try {
        const api = this.recipientType === 'contractor' ? importContractorTalkMe : importCustomerTalkMe;
        const response = await api(this.userId, false);
        const result = response.data || {};
        const updated = result.updated || 0;
        iwalyNotify.success({
          text: `Talk-Me: ${result.imported || 0} новых, ${updated} уточнено, ${result.skipped || 0} без изменений`,
        });
        await this.fetchData({ silent: true });
      } catch (e) {
        iwalyNotify.error({ text: 'Не удалось импортировать историю Talk-Me' });
      } finally { this.importing = false; }
    },
    receiptState(item) {
      const raw = String(
        item.deliveryStatus || item.legacyProviderStatus || item.status || '',
      ).trim().toLowerCase();
      if (item.readAt || raw === 'read' || raw === 'readed' || raw === 'seen' || raw === 'viewed') return 'read';
      if (item.deliveredAt || raw === 'delivered' || raw === 'delivery') return 'delivered';
      if (['failed', 'error', 'yellowcard', 'suspended', 'noaccount', 'notingroup'].includes(raw)) {
        return 'failed';
      }
      if (raw === 'pending') return 'pending';
      if (raw === 'sent' || raw === 'success' || raw === 'accepted') return 'sent';
      return '';
    },
    hasReceipt(item) {
      if (!item) return false;
      if (item.senderType === 'user' || item.source === 'user') return false;
      return this.receiptState(item) !== '';
    },
    deliveryLabel(item) {
      const state = this.receiptState(item);
      if (state === 'read') return item.readAt ? `Прочитано ${this.formatDateTime(item.readAt)}` : 'Прочитано';
      if (state === 'delivered') {
        return item.deliveredAt ? `Доставлено ${this.formatDateTime(item.deliveredAt)}` : 'Доставлено';
      }
      if (state === 'failed') return item.error ? `Ошибка: ${item.error}` : 'Ошибка доставки';
      if (state === 'pending') return 'Отправляется';
      if (state === 'sent') {
        if (item.channel === 'sms') return 'Принято SMS-провайдером';
        if (item.channel === 'push') return 'Передано Push-провайдеру';
        return 'Отправлено';
      }
      return 'Статус доставки неизвестен';
    },
    deliveryIcon(item) {
      const state = this.receiptState(item);
      if (state === 'read' || state === 'delivered') return '✓✓';
      if (state === 'failed') return '!';
      if (state === 'pending') return '…';
      return '✓';
    },
    async send() {
      if (!this.canSend || this.sending) return;

      let confirmUnverifiedEmail = false;

      if (this.channel === 'email'
        && this.selectedChannel
        && this.selectedChannel.replyMode === 'email_start'
        && this.selectedChannel.requiresFirstSendConfirmation) {
        const email = this.selectedChannel.recipientEmail || 'этот адрес';
        // Native confirmation is intentional: backend requires the same flag.
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm(
          `Email ${email} не подтверждён пользователем в iWALY. Проверьте адрес и подтвердите отправку.`,
        );
        if (!confirmed) return;
        confirmUnverifiedEmail = true;
      }

      if (this.channel === 'iwaly' && this.iwalyImportant) {
        // Native confirmation is intentional for the important-message safety gate.
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm('Отправить важное сообщение? Это экстренная мера.');
        if (!confirmed) return;
      }
      this.sending = true;
      try {
        if (this.iwalyFiles.length
          && this.selectedChannel
          && this.selectedChannel.capabilities
          && this.selectedChannel.capabilities.attachments) {
          await this.uploadIwalyFiles();
        }
        await this.sendApi(this.payload(confirmUnverifiedEmail));
        this.clearActiveComposer();
        this.iwalySendPush = true;
        iwalyNotify.success({ text: 'Сообщение принято к отправке' });
        await this.fetchData({ silent: true });
        this.$nextTick(this.scrollHistoryToBottom);
      } catch (error) {
        const description = error && error.response && error.response.data && error.response.data.description;
        iwalyNotify.error({ text: description || 'Не удалось отправить сообщение' });
      } finally { this.sending = false; }
    },
    toggleHistoryChannel(channel) {
      const index = this.historyChannelFilters.indexOf(channel);
      if (index >= 0) this.historyChannelFilters.splice(index, 1);
      else this.historyChannelFilters.push(channel);
    },
    isHistoryChannelSelected(channel) { return this.historyChannelFilters.includes(channel); },
    selectAllHistoryChannels() { this.historyChannelFilters = this.historyChannelOptions.map((item) => item.key); },
    clearHistoryChannels() { this.historyChannelFilters = []; },
    toggleDetails(id) { this.expandedMessageId = this.expandedMessageId === id ? null : id; },
    bubbleSide(item) {
      if (item.senderType === 'user') return 'left';
      if (item.senderType === 'system') return 'center';
      return 'right';
    },
    senderLabel(item) {
      if (item.senderType === 'user') return 'Пользователь';
      if (item.senderType === 'system') return 'iWALY · система';
      return 'Admin';
    },
    channelLabel(channel) {
      const labels = {
        iwaly: 'iWALY',
        web: 'Web',
        max: 'MAX',
        vk: 'VK',
        meta: 'Facebook + Instagram',
        external: 'Внешний канал',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        push: 'Push',
        email: 'Email',
      };
      return labels[channel] || channel || 'iWALY';
    },
    compactSystemText(value) {
      const text = String(value || '').replace(/\s+/g, ' ').trim();
      return text.length > 110 ? `${text.slice(0, 107)}…` : text;
    },
    technicalSummary(item) {
      const parts = [
        item.providerLabel,
        item.source,
        item.technical && item.technical.talkMeDialogId
          ? `dialog ${item.technical.talkMeDialogId}`
          : null,
      ].filter(Boolean);
      return parts.join(' · ');
    },
    messageHtml(value) {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };
      const escape = (text) => String(text || '').replace(/[&<>"']/g, (char) => entities[char]);
      const escaped = escape(value);
      return escaped
        .replace(
          /(https?:\/\/[^\s<]+)/g,
          '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
        )
        .replace(/\n/g, '<br>');
    },
    onChannelChanged() {
      this.emailReplyExternalContactId = null;
      this.emailReplyClientId = null;
      this.emailReplyDialogId = null;
      this.emailReplyContext = null;
      this.clearIwalyFiles();
    },
    attachmentErrorKey(attach) {
      if (!attach) return '';
      return String(attach.url || attach.id || '');
    },
    isRemoteProviderAttachment(attach) {
      return Boolean(attach && attach.source === 'talk_me');
    },
    remoteAttachmentRequested(attach) {
      if (!this.isRemoteProviderAttachment(attach)) return true;
      const key = this.attachmentErrorKey(attach);
      return key !== '' && Boolean(this.remoteAttachmentRequests[key]);
    },
    showRemoteAttachment(attach) {
      const key = this.attachmentErrorKey(attach);
      if (key !== '') this.$set(this.remoteAttachmentRequests, key, true);
    },
    attachmentFailed(attach) {
      const key = this.attachmentErrorKey(attach);
      return key !== '' && Boolean(this.attachmentImageErrors[key]);
    },
    markAttachmentFailed(attach) {
      const key = this.attachmentErrorKey(attach);
      if (key !== '') this.$set(this.attachmentImageErrors, key, true);
    },
    attachmentUnavailableLabel(attach) {
      return attach && attach.source === 'talk_me'
        ? 'Вложение Talk-Me недоступно'
        : 'Вложение недоступно';
    },
    compactEmailPreview(value, limit = 180) {
      const text = String(value || '')
        .replace(/\s+/g, ' ')
        .trim();

      return text.length > limit
        ? `${text.slice(0, limit - 1)}…`
        : text;
    },
    openEmail(item) {
      this.emailModalItem = item || null;
    },
    closeEmail() {
      this.emailModalItem = null;
    },
    replyToEmail(item) {
      if (!this.emailChannel || !this.emailChannel.enabled) return;

      const technical = item && item.technical
        ? item.technical
        : {};
      const directSmtp = this.emailChannel.firstSendTransport === 'smtp';

      // SMTP is the canonical Admin Email outbound transport. Talk-Me ids from
      // an inbound/history item are context only and must never silently reroute
      // the operator's Email reply back through Talk-Me.
      this.emailReplyExternalContactId = directSmtp
        ? null
        : (Number(
          (item && item.associatedExternalContactId)
            || this.emailChannel.externalContactId
            || 0,
        ) || null);
      this.emailReplyClientId = directSmtp
        ? null
        : (String(
          technical.talkMeClientId
            || this.emailChannel.clientId
            || '',
        ).trim() || null);
      this.emailReplyDialogId = directSmtp
        ? null
        : (Number(
          technical.talkMeDialogId
            || this.emailChannel.dialogId
            || 0,
        ) || null);
      this.emailReplyContext = {
        subject: (item && (item.emailSubject || item.title)) || 'Без темы',
        sentAt: item && (item.sentAt || item.createdAt),
        dialogId: this.emailReplyDialogId,
      };
      this.channel = 'email';
      this.emailModalItem = null;

      this.$nextTick(() => {
        const dock = this.$refs.composerDock;
        if (dock && typeof dock.scrollIntoView === 'function') {
          dock.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        const el = this.$el
          && this.$el.querySelector('.email-textarea');
        if (el) el.focus();
      });
    },

    formatDateTime(value) { const date = moment(value); return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '—'; },
    formatTime(value) { const date = moment(value); return date.isValid() ? date.format('HH:mm') : '—'; },
    formatDateLabel(date) {
      if (!date || !date.isValid()) return 'Без даты';
      const today = moment();
      if (date.isSame(today, 'day')) return 'Сегодня';
      if (date.isSame(moment().subtract(1, 'day'), 'day')) return 'Вчера';
      return date.format('D MMMM YYYY');
    },
    isHistoryNearBottom() {
      const el = this.$refs.historyScroll;
      if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    },
    onHistoryScroll() {
      this.userScrolledAwayFromBottom = !this.isHistoryNearBottom();
      this.persistHistoryScroll();
    },
    scrollHistoryToBottom() {
      const el = this.$refs.historyScroll;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      this.userScrolledAwayFromBottom = false;
      this.persistHistoryScroll();
    },
    startPolling() {
      this.stopPolling();
      this.autoRefreshActive = document.visibilityState !== 'hidden';
      this.pollTimer = window.setInterval(() => {
        if (document.visibilityState === 'visible' && !this.sending && !this.importing) this.fetchData({ silent: true });
      }, 8000);
    },
    stopPolling() { if (this.pollTimer) window.clearInterval(this.pollTimer); this.pollTimer = null; },
    handleVisibilityChange() {
      this.autoRefreshActive = document.visibilityState === 'visible';
      if (this.autoRefreshActive) this.fetchData({ silent: true });
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler(value, previousValue) {
        if (previousValue) this.persistHistoryScroll(previousValue);
        this.initialized = false;
        this.userScrolledAwayFromBottom = false;
        this.lastInboundMessageToken = null;
        this.emailReplyExternalContactId = null;
        this.emailReplyClientId = null;
        this.emailReplyDialogId = null;
        this.emailReplyContext = null;
        this.attachmentImageErrors = {};
        this.remoteAttachmentRequests = {};
        this.channel = 'iwaly';
        if (value) this.fetchData();
      },
    },
  },
};
</script>

<style scoped>
.communications-page { min-width: 0; padding: 20px; overflow-x: hidden; }
.communications-shell { width: 100%; min-width: 0; max-width: 1480px; display: flex; flex-direction: column; gap: 14px; }
.communications-header { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; }
.communications-head-copy { min-width: 0; flex: 1; }
.communications-title { margin: 0; font-size: 22px; line-height: 28px; }
.communications-subtitle, .composer-note, .auto-refresh-state { color: #7b8493; font-size: 12px; line-height: 18px; }
.recipient-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 7px; font-size: 12px; }
.consent-badge { border-radius: 12px; padding: 4px 9px; font-size: 11px; }
.consent-badge.ok { background: #e9f7ef; color: #25884f; }
.consent-badge.muted { background: #f2f3f5; color: #707782; }
.messenger-layout { display: grid; grid-template-columns: minmax(620px, 980px) minmax(330px, 400px); gap: 16px; align-items: start; min-width: 0; }
.messenger-card {
  min-width: 0;
  background: #fff;
  border: 1px solid #e0e4eb;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: var(--communications-viewport-height, 720px);
  min-height: 0;
  max-height: var(--communications-viewport-height, 720px);
}
.chat-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 14px; border-bottom: 1px solid #eef0f3; }
.chat-search-wrap { position: relative; flex: 1; min-width: 0; }
.chat-search { width: 100%; min-height: 38px; border: 1px solid #d8dde6; border-radius: 10px; padding: 8px 38px 8px 12px; outline: none; }
.search-count { position: absolute; right: 10px; top: 8px; color: #7b8493; font-size: 12px; }
.refresh-actions { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.filter-row { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 9px 14px; border-bottom: 1px solid #eef0f3; background: #fafbfc; }
.filter-label { font-size: 11px; color: #7b8493; margin-right: 2px; }
.filter-action { border: 0; background: transparent; color: #3b7c62; font-size: 11px; padding: 4px 5px; cursor: pointer; }
.filter-chip { border: 1px solid #d8dde6; background: #fff; border-radius: 999px; padding: 4px 9px; font-size: 11px; cursor: pointer; }
.filter-chip.active { border-color: #3d9b6d; background: #edf8f2; color: #25704d; }
.system-toggle { display: inline-flex; gap: 5px; align-items: center; font-size: 11px; color: #687386; margin-left: auto; }
.chat-history { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 10px 14px 18px; background: #f7f9fb; }
.date-divider { display: flex; justify-content: center; margin: 12px 0 9px; position: relative; z-index: 0; pointer-events: none; }
.date-divider span {
  background: rgba(255,255,255,.94);
  color: #6f7885;
  border: 1px solid #e1e5eb;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,.03);
}
.chat-row { display: flex; margin: 5px 0; }
.chat-row.left { justify-content: flex-start; }
.chat-row.right { justify-content: flex-end; }
.chat-row.center { justify-content: center; }
.chat-bubble {
  max-width: min(72%, 760px);
  min-width: 120px;
  background: #fff;
  border: 1px solid #e0e4eb;
  border-radius: 14px 14px 14px 5px;
  padding: 8px 10px 6px;
  box-shadow: 0 1px 2px rgba(18,31,53,.04);
}
.chat-row.right .chat-bubble { background: #eaf6f0; border-color: #d7eadf; border-radius: 14px 14px 5px 14px; }
.bubble-head { display: flex; gap: 7px; align-items: center; flex-wrap: wrap; margin-bottom: 4px; }
.bubble-name { color: #4e5b6d; font-size: 10px; font-weight: 700; }
.mini-channel { font-size: 9px; border-radius: 8px; padding: 2px 6px; background: #edf1f6; color: #596678; }
.channel-whatsapp { background: #e5f8ed; color: #25744c; }
.channel-iwaly { background: #e6f0ff; color: #39649b; }
.channel-web { background: #f1f2f4; color: #666e79; }
.channel-max { background: #efeaff; color: #65539b; }
.channel-sms { background: #e9f4ff; color: #28608f; }
.channel-push { background: #f0ebff; color: #65509a; }
.history-title { font-size: 12px; font-weight: 700; margin-bottom: 4px; }
.history-message { font-size: 12px; line-height: 17px; overflow-wrap: anywhere; }
.history-message >>> a { color: #2b6fc1; text-decoration: underline; }
.history-context {
  display: inline-flex;
  margin: 3px 0 5px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #eef4ff;
  color: #365f9d;
  font-size: 10px;
  font-weight: 700;
  text-decoration: none;
}
.history-images { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
.history-images img { width: 76px; height: 76px; object-fit: cover; border-radius: 8px; border: 1px solid #dfe4ea; }
.attachment-load-button {
  display: inline-flex;
  width: 120px;
  min-height: 54px;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 1px solid #cbd5df;
  border-radius: 8px;
  background: #fff;
  color: #49715e;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  cursor: pointer;
}
.attachment-load-button:hover { background: #f3f8f5; }
.attachment-unavailable {
  display: inline-flex;
  width: 120px;
  min-height: 54px;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 1px dashed #c8cfd8;
  border-radius: 8px;
  background: #f5f6f8;
  color: #7a828e;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
}
.bubble-foot { display: flex; justify-content: flex-end; align-items: center; gap: 5px; margin-top: 4px; color: #8a929e; font-size: 9px; }
.receipt-button, .details-button { border: 0; background: transparent; padding: 0 2px; color: #6b8d80; font-size: 10px; cursor: pointer; }
.receipt-button { font-size: 12px; font-weight: 800; letter-spacing: -3px; padding-right: 5px; }
.receipt-sent, .receipt-delivered { color: #8c969e; }
.receipt-read { color: #34b7f1; }
.receipt-failed { color: #c93434; letter-spacing: 0; }
.receipt-pending { color: #9aa3aa; letter-spacing: 0; }
.details-button { color: #8b929d; font-weight: 700; }
.technical-details { margin-top: 6px; border-top: 1px solid rgba(120,130,145,.18); padding-top: 6px; color: #717a87; font-size: 9px; line-height: 14px; }
.detail-row { display: flex; justify-content: space-between; gap: 10px; }
.system-event { display: flex; justify-content: center; margin: 4px 0; }
.system-event-main {
  max-width: 80%;
  border: 0;
  background: transparent;
  color: #8a929e;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  gap: 6px;
  align-items: center;
  text-align: center;
}
.system-event-dot { font-size: 16px; line-height: 8px; }
.system-event-time { white-space: nowrap; }
.system-details { max-width: 80%; margin: 3px auto 8px; text-align: center; }
.history-empty { padding: 36px 10px; text-align: center; color: #7b8493; font-size: 12px; }
.composer-dock { border-top: 1px solid #dfe4ea; background: #fff; padding: 10px 12px 12px; }
.email-reply-target {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin: 0 0 8px;
  padding: 7px 9px;
  border: 1px solid #b9d9ca;
  border-radius: 8px;
  background: #edf8f2;
  color: #356c55;
  font-size: 11px;
}
.email-reply-target > div { display: flex; gap: 6px; min-width: 0; }
.email-reply-target span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.email-reply-target small { color: #628170; white-space: nowrap; }
.composer-topbar { display: flex; align-items: center; gap: 8px; margin-bottom: 7px; }
.channel-select,
.communication-select,
.communication-input,
.communication-textarea {
  width: 100%;
  border: 1px solid #d5dae3;
  border-radius: 8px;
  background: #fff;
  padding: 8px 10px;
  outline: none;
}
.channel-select { max-width: 230px; min-height: 36px; }
.channel-warning-inline { color: #9b681d; font-size: 10px; flex: 1; }
.clear-composer { margin-left: auto; white-space: nowrap; }
.composer-textarea { min-height: 82px; max-height: 190px; resize: vertical; }
.composer-input-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 5px;
  flex-wrap: wrap;
}
.sms-editor {
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-height: 82px;
  max-height: 190px;
  border: 1px solid #d5dae3;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.sms-editor.disabled { background: #f5f6f8; }
.sms-prefix {
  flex: 0 0 auto;
  padding: 9px 0 8px 10px;
  color: #263341;
  font-weight: 700;
  line-height: 20px;
  user-select: none;
}
.sms-body-input {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 80px;
  max-height: 188px;
  border: 0;
  background: transparent;
  padding: 8px 10px 8px 4px;
  outline: none;
  resize: vertical;
}
.communication-input { min-height: 38px; margin-bottom: 7px; }
.composer-tools { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 7px; }
.iwaly-check.compact { display: inline-flex; gap: 5px; align-items: center; margin: 0; font-size: 11px; }
.important-warning {
  margin-top: 7px;
  padding: 7px 9px;
  border: 1px solid #f0d7a5;
  border-radius: 8px;
  background: #fff8e9;
  color: #8a5c16;
  font-size: 10px;
  line-height: 15px;
}
.whatsapp-first-warning {
  margin-bottom: 8px;
  padding: 8px 10px;
  border: 1px solid #df7777;
  border-radius: 8px;
  background: #fff0f0;
  color: #a32323;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
}
.whatsapp-textarea { margin-top: 7px; }
.quick-row, .smart-box { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
.chip, .template-item { border: 1px solid #d1d5db; background: #fff; border-radius: 999px; padding: 5px 9px; font-size: 10px; cursor: pointer; }
.templates-box { display: inline-block; margin-top: 7px; position: relative; }
.templates-summary { list-style: none; border: 1px solid #cbd4df; background: #f9fafb; border-radius: 8px; padding: 6px 10px; font-size: 11px; font-weight: 700; cursor: pointer; }
.templates-summary::-webkit-details-marker { display: none; }
.templates-list {
  position: absolute;
  left: 0;
  bottom: calc(100% + 5px);
  width: min(380px, 70vw);
  max-height: 260px;
  overflow-y: auto;
  z-index: 15;
  background: #fff;
  border: 1px solid #dce1e8;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(31,43,59,.12);
  padding: 6px;
}
.template-item { display: block; width: 100%; border-radius: 7px; text-align: left; margin: 3px 0; }
.message-preview { margin-top: 7px; padding: 8px 10px; background: #f8fafb; border: 1px solid #e1e5eb; border-radius: 8px; font-size: 11px; line-height: 16px; }
.composer-actions { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-top: 8px; }
.communication-send { min-width: 150px; }
.context-sidebar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  height: var(--communications-viewport-height, 720px);
  max-height: var(--communications-viewport-height, 720px);
  overflow-y: auto;
  overscroll-behavior: contain;
}
.sidebar-card {
  flex: 0 0 auto;
  background: #fff;
  border: 1px solid #e0e4eb;
  border-radius: 12px;
  overflow: hidden;
}
.sidebar-card-title {
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
  padding:10px 12px;
  border-bottom:1px solid #edf0f3;
  font-size:12px;
  font-weight:700;
}
.sidebar-card-title small { color:#8a929e; font-size:9px; font-weight:600; }
.reply-assistant-card { flex:0 0 auto; }
.reply-assistant-body { max-height:330px; }
.reply-section + .reply-section, .reply-section + .reply-templates { margin-top:10px; }
.reply-section-title { margin-bottom:5px; color:#7b8493; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.03em; }
.sidebar-reply {
  display:block;
  width:100%;
  margin:0 0 5px;
  padding:7px 8px;
  border:1px solid #e0e4ea;
  border-radius:8px;
  background:#fff;
  text-align:left;
  cursor:pointer;
}
.sidebar-reply:hover { border-color:#8fbca8; background:#f5fbf8; }
.sidebar-reply.smart { border-color:#d8eadf; background:#f7fcf9; }
.sidebar-reply b, .sidebar-reply span { display:block; }
.sidebar-reply b { color:#394655; font-size:10px; line-height:14px; }
.sidebar-reply span { margin-top:2px; color:#7b8493; font-size:9px; line-height:13px; }
.reply-templates { border-top:1px solid #edf0f3; padding-top:8px; }
.reply-templates > summary { cursor:pointer; color:#3b765e; font-size:10px; font-weight:700; }
.reply-template-list { margin-top:6px; }
.sidebar-template-select { margin:0; font-size:10px; min-height:34px; }
.sidebar-template-preview { margin-top:6px; padding:7px 8px; border-radius:7px; background:#f8fafb; color:#697482; font-size:9px; line-height:13px; }
.sidebar-card > summary { padding: 10px 12px; cursor: pointer; font-size: 12px; font-weight: 700; background: #fafbfc; border-bottom: 1px solid #edf0f3; }
.sidebar-card-body { padding: 10px 12px; max-height: 260px; overflow-y: auto; overscroll-behavior: contain; }
.orders-card .sidebar-card-body { max-height: 360px; }
.user-card .sidebar-card-body { max-height: none; overflow: visible; }
.context-row { display: flex; justify-content: space-between; gap: 10px; font-size: 11px; padding: 3px 0; }
.context-row span { color: #7b8493; flex: 0 0 auto; }
.context-row b { text-align: right; min-width: 0; overflow-wrap: anywhere; }
.truncate-value { max-width: 180px; }
.order-mini { display: block; padding: 8px 0; border-bottom: 1px solid #f1f3f5; font-size: 11px; color: #333d49; }
.order-summary { display: block; color: #333d49; text-decoration: none; }
.order-link:hover { text-decoration: none; color: #237553; }
.payment-actions { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 6px; }
.payment-action {
  border: 1px solid #d5dde6;
  background: #fff;
  color: #2c7558;
  border-radius: 7px;
  padding: 4px 7px;
  font-size: 9px;
  line-height: 13px;
  cursor: pointer;
}
.payment-action:hover { border-color: #74ad95; background: #f2faf6; }
.muted { color: #8a929e; font-size: 11px; }
.history-error { color: #b64646; }
.whitespace-pre-line { white-space: pre-line; }
@media (max-width: 1100px) {
  .messenger-layout { grid-template-columns: minmax(0, 1fr) 300px; }
  .chat-bubble { max-width: 82%; }
}
@media (max-width: 900px) {
  .communications-page { padding: 10px; }
  .communications-header { flex-direction: row; align-items: center; }
  .recipient-meta { display: none; }
  .messenger-layout { grid-template-columns: minmax(0, 1fr); }
  .messenger-card {
    height: var(--communications-viewport-height, 620px);
    min-height: 360px;
    max-height: var(--communications-viewport-height, 620px);
    order: 1;
  }
  .chat-history {
    flex: 1 1 auto;
    height: auto;
    min-height: 150px;
    max-height: none;
  }
  .composer-textarea { min-height: 92px; max-height: 170px; }
  .context-sidebar {
    order: 2;
    height: auto;
    max-height: none;
    overflow: visible;
  }
  .sidebar-card-body { max-height: 240px; }
  .orders-card .sidebar-card-body { max-height: 300px; }
  .chat-toolbar { flex-direction: row; align-items: center; }
  .refresh-actions { flex: 0 0 auto; justify-content: flex-end; }
  .auto-refresh-state { display: none; }
  .filter-row {
    overflow: visible;
    flex-wrap: wrap;
    align-content: flex-start;
  }
  .filter-action,
  .filter-chip { flex: 0 0 auto; }
  .system-toggle { margin-left: 4px; white-space: nowrap; }
  .chat-bubble { max-width: 90%; }
  .templates-list { position: fixed; left: 12px; right: 12px; bottom: 90px; width: auto; max-height: 45vh; }
  .composer-actions { align-items: center; }
}
.email-history-card {
  border:1px solid #dfe6ec;
  border-radius:9px;
  padding:9px 10px;
  background:#fbfcfd;
}
.email-card-title { display:flex; flex-direction:column; gap:2px; font-size:10px; }
.email-card-title b { font-size:11px; color:#394655; }
.email-card-preview { margin-top:6px; color:#687482; font-size:10px; line-height:14px; }
.email-card-actions { display:flex; gap:6px; margin-top:8px; }
.email-composer-meta { display:flex; justify-content:space-between; gap:10px; margin-bottom:6px; font-size:10px; color:#697482; }
.email-composer-meta b { color:#3b765e; }
.email-address-warning {
  margin:0 0 7px;
}
.email-signature-preview { margin-top:7px; border:1px solid #e1e6ec; border-radius:8px; padding:7px 9px; background:#fafbfc; }
.email-signature-preview summary { cursor:pointer; font-size:9px; font-weight:700; color:#3b765e; }
.email-signature-preview pre { max-height:150px; overflow:auto; margin:7px 0 0; white-space:pre-wrap; font-family:inherit; font-size:9px; line-height:13px; }
.provider-capability-note { margin-top:6px; color:#8a6b35; }
.email-modal-backdrop { position:fixed; inset:0; z-index:2200; display:flex; align-items:center; justify-content:center; padding:24px; background:rgba(24,31,40,.52); }
.email-modal {
  width:min(980px,96vw);
  height:min(820px,92vh);
  display:flex;
  flex-direction:column;
  background:#fff;
  border-radius:12px;
  box-shadow:0 24px 70px rgba(24,32,44,.3);
  overflow:hidden;
}
.email-modal-head { flex:0 0 auto; display:flex; justify-content:space-between; gap:18px; padding:18px 20px; border-bottom:1px solid #e8ebef; }
.email-modal-head small { color:#3b765e; font-weight:700; }
.email-modal-head h3 { margin:4px 0; font-size:17px; }
.email-modal-meta { color:#8a929e; font-size:10px; }
.email-modal-close { width:36px; height:36px; border:0; border-radius:8px; background:#f2f4f6; font-size:24px; cursor:pointer; }
.email-modal-body { flex:1 1 auto; min-height:0; overflow:auto; padding:20px; }
.email-full-text { font-size:13px; line-height:1.55; overflow-wrap:anywhere; }
.email-modal-actions { flex:0 0 auto; display:flex; justify-content:flex-end; gap:8px; padding:12px 20px; border-top:1px solid #e8ebef; }
.email-modal-images { margin-top:16px; }


@media (max-width: 600px) {
  .communications-page { padding: 6px; }
  .communications-header { display: none; }
  .communications-shell { gap: 8px; }
  .messenger-card {
    height: var(--communications-viewport-height, 650px);
    min-height: 500px;
    max-height: var(--communications-viewport-height, 650px);
    border-radius: 10px;
  }
  .context-sidebar {
    height: auto;
    min-height: 0;
    max-height: none;
  }
  .chat-toolbar { gap: 7px; padding: 8px; }
  .chat-search { min-height: 36px; padding: 7px 34px 7px 10px; font-size: 12px; }
  .refresh-actions .btn { min-height: 36px; padding-left: 11px; padding-right: 11px; white-space: nowrap; }
  .filter-row { gap: 5px; padding: 7px 8px; }
  .filter-label { display: none; }
  .filter-action { padding: 5px 6px; font-size: 10px; }
  .filter-chip { padding: 5px 8px; font-size: 10px; }
  .system-toggle {
    flex: 1 0 100%;
    margin: 1px 0 0;
    font-size: 10px;
  }
  .chat-history {
    flex: 1 1 auto;
    height: auto;
    min-height: 130px;
    max-height: none;
    padding: 7px 8px 10px;
  }
  .composer-dock {
    flex: 0 0 auto;
    padding: 7px 8px 8px;
    box-shadow: 0 -4px 12px rgba(31,43,59,.05);
    position: relative;
    z-index: 2;
  }
  .composer-topbar { gap: 6px; margin-bottom: 6px; flex-wrap: wrap; }
  .channel-select { max-width: 100%; min-height: 34px; font-size: 11px; }
  .composer-textarea { min-height: 82px; max-height: 130px; resize: none; }
  .sms-editor { min-height: 82px; max-height: 130px; }
  .sms-body-input { min-height: 80px; max-height: 128px; resize: none; }
  .composer-input-meta { margin-top: 3px; font-size: 9px; }
  .composer-tools { gap: 6px; margin-top: 5px; }
  .composer-actions { gap: 6px; margin-top: 6px; }
  .composer-actions .composer-note { font-size: 9px; line-height: 13px; }
  .clear-composer { margin-left: 0; }
  .communication-send { min-width: 118px; margin-left: auto; }
  .chat-bubble { max-width: 94%; }
  .bubble-name { font-size: 9px; }
  .history-message { font-size: 11px; line-height: 16px; }
  .sidebar-card > summary { padding: 9px 10px; }
}

.web-target-note {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.identity-summary {
  display: flex;
  gap: 8px;
  align-items: baseline;
  flex-wrap: wrap;
  border: 1px solid #e2e6eb;
  border-left-width: 4px;
  border-radius: 9px;
  padding: 8px 11px;
  font-size: 10px;
  line-height: 14px;
  background: #fff;
}
.identity-summary.success { border-left-color: #4f9b70; background: #f7fcf9; }
.identity-summary.warning { border-left-color: #d8a241; background: #fffaf0; }
.identity-summary.danger { border-left-color: #c76c68; background: #fff7f7; }
.identity-summary span { color: #687385; }
.identity-message-badge {
  border-radius: 999px;
  padding: 2px 6px;
  font-size: 8px;
  font-weight: 700;
  white-space: nowrap;
}
.identity-message-badge.success { background: #eaf7ef; color: #2f7650; }
.identity-message-badge.warning { background: #fff2d9; color: #7e5a20; }
.identity-message-badge.danger { background: #fdebea; color: #8a403b; }
.privacy-channel-warning {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0 0 7px;
  padding: 7px 9px;
  border: 1px solid #efd79f;
  border-radius: 8px;
  background: #fff9e9;
  color: #73551d;
  font-size: 9px;
  line-height: 13px;
}

</style>
